import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";

// Cartoon UFO with shape variants. Default is the classic saucer.
// Cosmetic visual fields:
//   shape: "saucer" | "triangle" | "rocket" | "crystal" | "orb" | "battle"
//   body:  hex color for the main body / outer hull
//   dome:  hex color for the dome / top accent
//   glow:  hex color for emissive accents and underbody light
function UFO({ position = [0, 10, 0], rotation = [0, 0, 0], skin }) {
  const body = skin?.body || "#cfd4dc";
  const dome = skin?.dome || "#7ed957";
  const glow = skin?.glow || "#7ed957";
  const shape = skin?.shape || "saucer";
  const spinRef = useRef();
  const lightsRef = useRef([]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (spinRef.current) {
      spinRef.current.rotation.y = t * 0.6;
    }
    lightsRef.current.forEach((m, i) => {
      if (m && m.material) {
        const phase = (t * 4 + i * 0.7) % 2;
        m.material.emissiveIntensity = phase < 1 ? 1.8 : 0.3;
      }
    });
  });

  return (
    <group position={position} rotation={rotation}>
      {shape === "saucer" && <SaucerShape body={body} dome={dome} glow={glow} spinRef={spinRef} lightsRef={lightsRef} />}
      {shape === "triangle" && <TriangleShape body={body} dome={dome} glow={glow} spinRef={spinRef} lightsRef={lightsRef} />}
      {shape === "rocket" && <RocketShape body={body} dome={dome} glow={glow} lightsRef={lightsRef} />}
      {shape === "crystal" && <CrystalShape body={body} dome={dome} glow={glow} spinRef={spinRef} lightsRef={lightsRef} />}
      {shape === "orb" && <OrbShape body={body} dome={dome} glow={glow} spinRef={spinRef} lightsRef={lightsRef} />}
      {shape === "battle" && <BattleShape body={body} dome={dome} glow={glow} spinRef={spinRef} lightsRef={lightsRef} />}
      {/* Point light under the ship for a soft underglow on the ground. */}
      <pointLight position={[0, -1, 0]} intensity={1.2} color={glow} distance={8} />
    </group>
  );
}

// ----- Shape: classic saucer (default) -----
function SaucerShape({ body, dome, glow, spinRef, lightsRef }) {
  const lights = [];
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const x = Math.cos(angle) * 1.55;
    const z = Math.sin(angle) * 1.55;
    lights.push(
      <mesh key={i} position={[x, -0.05, z]} ref={(el) => (lightsRef.current[i] = el)}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#fff66b" emissive="#fff66b" emissiveIntensity={1.5} toneMapped={false} />
      </mesh>
    );
  }
  return (
    <>
      <group ref={spinRef}>
        <mesh castShadow position={[0, 0, 0]}>
          <cylinderGeometry args={[1.8, 2.1, 0.4, 24]} />
          <meshStandardMaterial color={body} emissive="#5a606a" emissiveIntensity={0.25} metalness={0.85} roughness={0.2} />
        </mesh>
        <mesh position={[0, -0.22, 0]}>
          <cylinderGeometry args={[2.0, 1.7, 0.15, 24]} />
          <meshStandardMaterial color="#9aa0aa" metalness={0.7} roughness={0.35} />
        </mesh>
        {lights}
      </group>
      <mesh position={[0, 0.55, 0]} castShadow>
        <sphereGeometry args={[1.0, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={dome} emissive={glow} emissiveIntensity={0.4} transparent opacity={0.7} metalness={0.2} roughness={0.1} />
      </mesh>
      <mesh position={[0, 0.2, 0]}>
        <torusGeometry args={[1.0, 0.08, 8, 24]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0, 1.7, 0]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#ff5b6b" emissive="#ff5b6b" emissiveIntensity={1.2} toneMapped={false} />
      </mesh>
      <mesh position={[0, 1.45, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.5, 6]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.8} roughness={0.3} />
      </mesh>
    </>
  );
}

// ----- Shape: stealth triangle (4-sided pyramid pointing forward) -----
function TriangleShape({ body, dome, glow, spinRef, lightsRef }) {
  const lights = [];
  const tipPositions = [
    [0, -0.1, 1.9], [1.7, -0.1, -1.2], [-1.7, -0.1, -1.2],
  ];
  tipPositions.forEach((p, i) => {
    lights.push(
      <mesh key={i} position={p} ref={(el) => (lightsRef.current[i] = el)}>
        <sphereGeometry args={[0.18, 8, 8]} />
        <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
    );
  });
  return (
    <>
      <group ref={spinRef}>
        <mesh castShadow position={[0, 0, 0]}>
          {/* Flat tetrahedron base — cylinderGeometry with 3 sides */}
          <cylinderGeometry args={[2.1, 2.1, 0.35, 3]} />
          <meshStandardMaterial color={body} emissive="#0a0a14" emissiveIntensity={0.3} metalness={0.9} roughness={0.25} />
        </mesh>
        <mesh position={[0, -0.2, 0]}>
          <cylinderGeometry args={[2.0, 1.6, 0.15, 3]} />
          <meshStandardMaterial color="#1a1a26" metalness={0.7} roughness={0.4} />
        </mesh>
        {lights}
      </group>
      {/* Cockpit pod (low, sleek) */}
      <mesh position={[0, 0.45, 0.3]} castShadow>
        <sphereGeometry args={[0.6, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={dome} emissive={dome} emissiveIntensity={0.6} transparent opacity={0.75} metalness={0.3} roughness={0.08} />
      </mesh>
      {/* Twin tail fins */}
      {[-0.9, 0.9].map((x, i) => (
        <mesh key={i} position={[x, 0.25, -1.2]} rotation={[0, 0, x > 0 ? -0.3 : 0.3]} castShadow>
          <boxGeometry args={[0.1, 0.5, 0.7]} />
          <meshStandardMaterial color={body} metalness={0.8} roughness={0.3} />
        </mesh>
      ))}
    </>
  );
}

// ----- Shape: rocket pod (vertical capsule) -----
function RocketShape({ body, dome, glow, lightsRef }) {
  const flameRef = useRef();
  useFrame((s) => {
    if (flameRef.current) {
      const t = s.clock.elapsedTime;
      flameRef.current.scale.y = 1 + Math.sin(t * 18) * 0.15;
    }
  });
  return (
    <>
      {/* Capsule body */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.85, 0.85, 1.8, 18]} />
        <meshStandardMaterial color={body} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Nose cone */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <coneGeometry args={[0.85, 1.2, 18]} />
        <meshStandardMaterial color={dome} emissive={dome} emissiveIntensity={0.5} metalness={0.5} roughness={0.2} />
      </mesh>
      {/* Window porthole */}
      <mesh position={[0, 0.6, 0.86]} ref={(el) => (lightsRef.current[0] = el)}>
        <sphereGeometry args={[0.22, 16, 12]} />
        <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={1.8} transparent opacity={0.9} toneMapped={false} />
      </mesh>
      {/* 3 fins */}
      {[0, 120, 240].map((deg, i) => {
        const rad = (deg * Math.PI) / 180;
        return (
          <mesh key={i} position={[Math.sin(rad) * 0.9, -0.5, Math.cos(rad) * 0.9]} rotation={[0, -rad, 0]} castShadow>
            <boxGeometry args={[0.1, 0.9, 0.7]} />
            <meshStandardMaterial color={body} metalness={0.7} roughness={0.3} />
          </mesh>
        );
      })}
      {/* Engine flame */}
      <mesh position={[0, -1.05, 0]} ref={flameRef}>
        <coneGeometry args={[0.5, 1.2, 14]} />
        <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={2} transparent opacity={0.75} toneMapped={false} />
      </mesh>
    </>
  );
}

// ----- Shape: crystal / diamond -----
function CrystalShape({ body, dome, glow, spinRef, lightsRef }) {
  return (
    <>
      <group ref={spinRef}>
        {/* Top half: pointed up octahedron */}
        <mesh castShadow position={[0, 0.4, 0]}>
          <octahedronGeometry args={[1.3, 0]} />
          <meshStandardMaterial color={body} emissive={glow} emissiveIntensity={0.35} metalness={0.55} roughness={0.1} transparent opacity={0.85} />
        </mesh>
        {/* Inner brighter core (smaller) */}
        <mesh position={[0, 0.4, 0]} ref={(el) => (lightsRef.current[0] = el)}>
          <octahedronGeometry args={[0.55, 0]} />
          <meshStandardMaterial color={dome} emissive={dome} emissiveIntensity={1.8} toneMapped={false} />
        </mesh>
        {/* Orbiting ring (thin torus, ref-rotated by spinRef parent) */}
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0.4, 0]}>
          <torusGeometry args={[1.55, 0.05, 8, 36]} />
          <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={1.2} toneMapped={false} />
        </mesh>
      </group>
    </>
  );
}

// ----- Shape: plasma orb (sphere with halo) -----
function OrbShape({ body, dome, glow, spinRef, lightsRef }) {
  return (
    <>
      <group ref={spinRef}>
        {/* Outer translucent shell */}
        <mesh castShadow>
          <sphereGeometry args={[1.4, 28, 20]} />
          <meshStandardMaterial color={body} emissive={glow} emissiveIntensity={0.45} transparent opacity={0.55} metalness={0.3} roughness={0.15} />
        </mesh>
        {/* Inner core */}
        <mesh ref={(el) => (lightsRef.current[0] = el)}>
          <sphereGeometry args={[0.7, 20, 16]} />
          <meshStandardMaterial color={dome} emissive={dome} emissiveIntensity={2} toneMapped={false} />
        </mesh>
        {/* Equatorial halo */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.7, 0.08, 8, 36]} />
          <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={1.3} toneMapped={false} />
        </mesh>
        {/* 2nd tilted halo */}
        <mesh rotation={[Math.PI / 2, 0, Math.PI / 4]}>
          <torusGeometry args={[1.55, 0.04, 8, 36]} />
          <meshStandardMaterial color={dome} emissive={dome} emissiveIntensity={1} toneMapped={false} />
        </mesh>
      </group>
    </>
  );
}

// ----- Shape: battle cruiser (boxy military) -----
function BattleShape({ body, dome, glow, spinRef, lightsRef }) {
  const lights = [];
  for (let i = 0; i < 4; i++) {
    const x = i < 2 ? -1.6 : 1.6;
    const z = i % 2 === 0 ? -0.8 : 0.8;
    lights.push(
      <mesh key={i} position={[x, -0.05, z]} ref={(el) => (lightsRef.current[i] = el)}>
        <boxGeometry args={[0.2, 0.2, 0.2]} />
        <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={1.6} toneMapped={false} />
      </mesh>
    );
  }
  return (
    <>
      <group ref={spinRef}>
        {/* Main hull (rectangular) */}
        <mesh castShadow>
          <boxGeometry args={[3.5, 0.6, 2.2]} />
          <meshStandardMaterial color={body} metalness={0.85} roughness={0.4} />
        </mesh>
        {/* Lower hull */}
        <mesh position={[0, -0.4, 0]}>
          <boxGeometry args={[3.0, 0.25, 1.7]} />
          <meshStandardMaterial color="#2c3318" metalness={0.7} roughness={0.5} />
        </mesh>
        {lights}
      </group>
      {/* Command bridge */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <boxGeometry args={[1.2, 0.5, 1.0]} />
        <meshStandardMaterial color={dome} metalness={0.7} roughness={0.3} />
      </mesh>
      {/* Bridge dome window */}
      <mesh position={[0, 0.85, 0]}>
        <sphereGeometry args={[0.4, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#88ddff" emissive="#88ddff" emissiveIntensity={0.7} transparent opacity={0.8} toneMapped={false} />
      </mesh>
      {/* Twin cannons */}
      {[-1.0, 1.0].map((x, i) => (
        <mesh key={i} position={[x, 0.45, 1.0]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.1, 0.1, 0.9, 10]} />
          <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.3} />
        </mesh>
      ))}
    </>
  );
}

export default React.memo(UFO);
